import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useBranch } from '../contexts/BranchContext'
import { customerService, washService } from '../services/firebase'
import { voucherProgramService } from '../services/voucherProgramService'
import { Users, Search, Phone, Mail, Calendar, Star, Filter, Eye } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'
import { formatRand, getVoucherLoyalty, parseStoredDate } from '../utils/voucherLoyalty'

function getDateFromTimestamp(timestamp) {
  return parseStoredDate(timestamp) || new Date()
}

export default function CustomerPage() {
  const { currentUser, role } = useAuth()
  const { currentBranch, isViewingAllBranches, getEffectiveBranchId } = useBranch()
  const navigate = useNavigate()
  const [customers, setCustomers] = useState([])
  const [filteredCustomers, setFilteredCustomers] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [customerWashes, setCustomerWashes] = useState([])
  const [showModal, setShowModal] = useState(false)
  const [filterBy, setFilterBy] = useState('all')
  const [mergeCandidateId, setMergeCandidateId] = useState('')
  const [mergeLoading, setMergeLoading] = useState(false)
  const [accountSaving, setAccountSaving] = useState(false)
  const [voucherProgram, setVoucherProgram] = useState(null)

  const startSubscriptionCheckout = () => {
    if (!selectedCustomer?.id) return
    navigate(`/pos?customerId=${encodeURIComponent(selectedCustomer.id)}&tab=subscription`)
    setShowModal(false)
  }

  useEffect(() => {
    loadCustomers()
  }, [currentBranch, role])

  useEffect(() => {
    filterCustomers()
  }, [customers, searchTerm, filterBy, voucherProgram])

  const loadCustomers = async () => {
    // Don't load data if currentBranch is not yet available
    if (!currentBranch) {
      return
    }
    
    try {
      setLoading(true)
      const program = await voucherProgramService.get().catch(() => null)
      setVoucherProgram(program)
      // Owners: full CRM list (customers can visit any branch).
      // Managers/staff: their branch + unassigned/legacy (no branchId).
      if (role === 'owner' || isViewingAllBranches()) {
        const customerData = await customerService.getAllCustomers()
        setCustomers(customerData || [])
      } else {
        const customerData = await customerService.getCustomersByBranch(currentBranch.id)
        setCustomers(customerData || [])
      }
    } catch (error) {
      console.error('Error loading customers:', error)
      toast.error('Failed to load customers')
    } finally {
      setLoading(false)
    }
  }

  const filterCustomers = () => {
    // Hide merged duplicates and empty signup shells only.
    // Completed profiles may still have isSystemProfile from older onboarding.
    let filtered = customers.filter((c) => {
      if (c?.mergedInto) return false
      if (!c?.isSystemProfile) return true
      const hasIdentity =
        Boolean(String(c?.name || '').trim()) ||
        Boolean(String(c?.phone || '').trim()) ||
        Boolean(String(c?.email || '').trim())
      return hasIdentity
    })

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(customer => 
        customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.phone?.includes(searchTerm) ||
        customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(customer.customerCode || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Additional filters
    if (filterBy === 'loyalty') {
      filtered = filtered.filter((customer) => {
        const v = getVoucherLoyalty(customer, voucherProgram)
        return v.hasVoucher || v.isEarning
      })
    } else if (filterBy === 'subscription') {
      filtered = filtered.filter(customer => customer.hasActiveSubscription)
    } else if (filterBy === 'on_account') {
      filtered = filtered.filter(customer => customer.allowOnAccount)
    }

    setFilteredCustomers(filtered)
  }

  const listableCustomers = customers.filter((c) => {
    if (c?.mergedInto) return false
    if (!c?.isSystemProfile) return true
    return (
      Boolean(String(c?.name || '').trim()) ||
      Boolean(String(c?.phone || '').trim()) ||
      Boolean(String(c?.email || '').trim())
    )
  })

  const viewCustomerDetails = async (customer) => {
    setSelectedCustomer(customer)
    setCustomerWashes([])
    setMergeCandidateId('')
    setShowModal(true)
    try {
      setLoading(true)
      const branchId = role === 'owner' || isViewingAllBranches() ? null : getEffectiveBranchId()
      const washes = await washService.getCustomerWashes(customer.id, branchId)
      setCustomerWashes(washes || [])
    } catch (error) {
      console.error('Error loading customer wash history:', error)
      setCustomerWashes([])
    } finally {
      setLoading(false)
    }
  }

  const findMergeCandidates = (customer) => {
    if (!customer) return []
    const email = String(customer.email || '').trim().toLowerCase()
    const phone = String(customer.phone || '').trim()
    return customers
      .filter((c) => c?.id && c.id !== customer.id && !c?.mergedInto && !c?.isSystemProfile)
      .filter((c) => {
        const e = String(c.email || '').trim().toLowerCase()
        const p = String(c.phone || '').trim()
        return (email && e && e === email) || (phone && p && p === phone)
      })
  }

  const saveOnAccountSettings = async (allowOnAccount) => {
    if (!selectedCustomer?.id) return
    try {
      setAccountSaving(true)
      await customerService.updateCustomer(selectedCustomer.id, {
        allowOnAccount,
        updatedAt: new Date(),
      })
      const updated = { ...selectedCustomer, allowOnAccount }
      setSelectedCustomer(updated)
      setCustomers((prev) =>
        prev.map((c) => (c.id === selectedCustomer.id ? { ...c, allowOnAccount } : c))
      )
      toast.success(allowOnAccount ? 'On-account billing enabled' : 'On-account billing disabled')
    } catch (e) {
      console.error(e)
      toast.error('Failed to update on-account settings')
    } finally {
      setAccountSaving(false)
    }
  }

  const runMerge = async () => {
    if (!selectedCustomer?.id || !mergeCandidateId) return
    try {
      setMergeLoading(true)
      await customerService.mergeCustomers({
        sourceCustomerId: mergeCandidateId,
        targetCustomerId: selectedCustomer.id,
      })
      toast.success('Merged customer successfully')
      await loadCustomers()
      // Refresh wash list
      const branchId = role === 'owner' || isViewingAllBranches() ? null : getEffectiveBranchId()
      const washes = await washService.getCustomerWashes(selectedCustomer.id, branchId)
      setCustomerWashes(washes)
      setMergeCandidateId('')
    } catch (e) {
      console.error('Merge failed:', e)
      toast.error('Merge failed')
    } finally {
      setMergeLoading(false)
    }
  }

  const getCustomerStats = (customer) => {
    const totalWashes = customerWashes.length
    const avgRating = customerWashes.filter(w => w.rating).length > 0 
      ? (customerWashes.reduce((sum, w) => sum + (w.rating || 0), 0) / customerWashes.filter(w => w.rating).length).toFixed(1)
      : 'N/A'
    
    return { totalWashes, avgRating }
  }

  if (loading && customers.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Customer Management</h1>
        <p className="text-gray-600">
          {role === 'owner' || isViewingAllBranches()
            ? `All customers · ${filteredCustomers.length} shown`
            : `${currentBranch?.name || 'Branch'} (+ unassigned) · ${filteredCustomers.length} shown`}
        </p>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search by name, phone, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Customers</option>
              <option value="loyalty">Earning / has voucher</option>
              <option value="subscription">Active Subscription</option>
              <option value="on_account">On-account billing</option>
            </select>
          </div>
        </div>
      </div>

      {/* Customer Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Customers</p>
              <p className="text-2xl font-bold text-gray-900">{listableCustomers.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Star className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Earning vouchers</p>
              <p className="text-2xl font-bold text-gray-900">
                {listableCustomers.filter((c) => {
                  const v = getVoucherLoyalty(c, voucherProgram)
                  return v.hasVoucher || v.isEarning
                }).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Calendar className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Subscriptions</p>
              <p className="text-2xl font-bold text-gray-900">
                {listableCustomers.filter(c => c.hasActiveSubscription).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Filter className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Filtered</p>
              <p className="text-2xl font-bold text-gray-900">{filteredCustomers.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Customer List */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Voucher progress
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Visit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCustomers.map((customer) => (
                <tr key={customer.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-blue-600 font-medium">
                          {customer.name?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {customer.name || 'Unknown'}
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: {customer.id.slice(-6)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 flex items-center">
                      <Phone className="h-4 w-4 mr-1" />
                      {customer.phone}
                    </div>
                    {customer.email && (
                      <div className="text-sm text-gray-500 flex items-center">
                        <Mail className="h-4 w-4 mr-1" />
                        {customer.email}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {(() => {
                      const v = getVoucherLoyalty(customer, voucherProgram)
                      return (
                        <div>
                          <div className="flex items-center">
                            <Star className="h-4 w-4 text-yellow-400 mr-1" />
                            <span className="text-sm font-medium text-gray-900">
                              {v.progressLabel}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            Balance {formatRand(v.balance)}
                          </div>
                        </div>
                      )
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {(() => {
                      const v = getVoucherLoyalty(customer, voucherProgram)
                      const label = customer.hasActiveSubscription
                        ? 'Subscriber'
                        : v.hasVoucher
                          ? 'Voucher'
                          : v.isEarning
                            ? 'Earning'
                            : 'Regular'
                      const cls = customer.hasActiveSubscription
                        ? 'bg-green-100 text-green-800'
                        : v.hasVoucher
                          ? 'bg-yellow-100 text-yellow-800'
                          : v.isEarning
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                      return (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${cls}`}>
                          {label}
                        </span>
                      )
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {(() => {
                      const d = parseStoredDate(customer.lastVisit)
                      return d ? d.toLocaleDateString('en-ZA') : 'Never'
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => viewCustomerDetails(customer)}
                      className="text-blue-600 hover:text-blue-900 flex items-center"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Customer Details Modal */}
      {showModal && selectedCustomer && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Customer Details: {selectedCustomer.name}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                {/* Customer Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Contact Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Phone</p>
                      <p className="font-medium">{selectedCustomer.phone}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-medium">{selectedCustomer.email || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Voucher progress</p>
                      <p className="font-medium">
                        {getVoucherLoyalty(selectedCustomer, voucherProgram).progressLabel}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Voucher balance</p>
                      <p className="font-medium">
                        {formatRand(getVoucherLoyalty(selectedCustomer, voucherProgram).balance)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Member Since</p>
                      <p className="font-medium">
                        {selectedCustomer.createdAt ? getDateFromTimestamp(selectedCustomer.createdAt).toLocaleDateString() : 'Unknown'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">On-account billing</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Trade customers can check in without paying upfront. Balance is settled monthly or per your agreement.
                  </p>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <p className="text-sm text-gray-600">Outstanding balance</p>
                      <p className="text-lg font-semibold text-gray-900 tabular-nums">
                        R{Number(selectedCustomer.accountBalance || 0).toFixed(2)}
                      </p>
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={Boolean(selectedCustomer.allowOnAccount)}
                        disabled={accountSaving}
                        onChange={(e) => saveOnAccountSettings(e.target.checked)}
                      />
                      <span>Allow on-account billing</span>
                    </label>
                  </div>
                </div>

                {/* Merge duplicates */}
                {findMergeCandidates(selectedCustomer).length > 0 && (
                  <div className="bg-white border border-gray-200 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Merge duplicate customer</h4>
                    <p className="text-sm text-gray-600 mb-3">
                      If the same person exists twice (same email/phone), merge the duplicate into this record.
                      This moves washes/vouchers/subscriptions and hides the duplicate.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <select
                        value={mergeCandidateId}
                        onChange={(e) => setMergeCandidateId(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="">Select duplicate to merge…</option>
                        {findMergeCandidates(selectedCustomer).map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name || 'Unnamed'} · {c.phone || 'No phone'} · {c.email || 'No email'} · ID:{c.id}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={runMerge}
                        disabled={!mergeCandidateId || mergeLoading}
                        className="px-4 py-2 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {mergeLoading ? 'Merging…' : 'Merge'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Subscriptions */}
                <div className="bg-white border border-gray-200 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Subscriptions</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Sell subscriptions from POS so payment + activation happen together.
                  </p>
                  <button
                    onClick={startSubscriptionCheckout}
                    className="w-full px-4 py-3 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700"
                  >
                    Open POS (Subscriptions)
                  </button>
                </div>

                {/* Wash History */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Wash History</h4>
                  {customerWashes.length > 0 ? (
                    <div className="max-h-60 overflow-y-auto">
                      <div className="space-y-2">
                        {customerWashes.slice(0, 10).map((wash) => (
                          <div key={wash.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium">{wash.serviceType}</p>
                              <p className="text-sm text-gray-600">
                                {getDateFromTimestamp(wash.createdAt).toLocaleDateString()}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium">R{wash.amount}</p>
                              {wash.rating && (
                                <div className="flex items-center">
                                  <Star className="h-4 w-4 text-yellow-400 mr-1" />
                                  <span className="text-sm">{wash.rating}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500">No wash history available</p>
                  )}
                </div>

                {/* Stats */}
                {customerWashes.length > 0 && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Customer Stats</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Total Washes</p>
                        <p className="text-lg font-bold text-blue-600">{getCustomerStats(selectedCustomer).totalWashes}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Average Rating</p>
                        <p className="text-lg font-bold text-blue-600">{getCustomerStats(selectedCustomer).avgRating}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Total Spent</p>
                        <p className="text-lg font-bold text-blue-600">
                          R{customerWashes.reduce((sum, wash) => sum + (wash.amount || 0), 0)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 